(() => {
  'use strict';
  document.documentElement.classList.replace('no-js', 'js');

  let header = document.querySelector('#site-header');
  let headerScrollPending = false;
  const updateHeader = () => { header?.classList.toggle('is-scrolled', window.scrollY > 24); headerScrollPending = false; };
  updateHeader();
  window.addEventListener('scroll', () => { if (!headerScrollPending) { headerScrollPending = true; requestAnimationFrame(updateHeader); } }, { passive: true });
  const one = (selector, root = document) => root.querySelector(selector);
  const all = (selector, root = document) => [...root.querySelectorAll(selector)];
  const root = window.Shopify?.routes?.root || '/';
  const route = path => `${root}${path}`;
  const currency = window.Shopify?.currency?.active || document.body.dataset.currency || 'USD';
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency
  }).format(cents / 100);
  const checkoutMoney = (amount, checkoutCurrency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency: checkoutCurrency
  }).format(amount / (['BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG', 'RWF', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'].includes(checkoutCurrency.toUpperCase()) ? 1 : 100));
  const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[character]);
  const announce = message => { const region = one('#live-region'); if (region) region.textContent = message; };
  const storage = {
    read(key) { try { const value = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(value) ? value : []; } catch { return []; } },
    write(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* Browsing still works without local storage. */ } }
  };

  let activeEmbeddedCheckout = null;
  let checkoutBusy = false;
  let checkoutToken = 0;
  let cartUpdatePromise = Promise.resolve();
  const loadStripeJs = window.VaytrixCommerce.createStripeLoader(window, document);
  const setCheckoutBusy = value => {
    checkoutBusy = value;
    one('[data-vaytrix-checkout-loading]')?.toggleAttribute('hidden', !value);
    all('[data-cart-checkout],[data-vaytrix-buy-now],[data-vaytrix-mobile-buy]').forEach(button => {
      if (value) { button.dataset.checkoutDisabled = String(button.disabled); button.disabled = true; }
      else if ('checkoutDisabled' in button.dataset) { button.disabled = button.dataset.checkoutDisabled === 'true'; delete button.dataset.checkoutDisabled; }
    });
  };
  const drawer = one('#cart-drawer');
  const backdrop = one('#drawer-backdrop');
  const modal = one('#quick-view');
  let returnFocus = null;
  let modalFocus = null;
  const setBackgroundInert = value => all('#MainContent,.site-header,.footer,.announcement').forEach(element => { element.inert = value; });

  function openDrawer(refresh = true) {
    if (!drawer) return;
    if (refresh) refreshCart().catch(() => announce('Bag could not be refreshed.'));
    closeModal(false);
    returnFocus = document.activeElement;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    backdrop.hidden = false;
    document.body.classList.add('overlay-open');
    setBackgroundInert(true);
    one('[data-close-drawer]', drawer)?.focus();
  }
  function closeDrawer(restore = true) {
    if (!drawer?.classList.contains('open')) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    backdrop.hidden = true;
    document.body.classList.remove('overlay-open');
    setBackgroundInert(false);
    if (restore) returnFocus?.focus();
  }
  function closeModal(restore = true) {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    modal.replaceChildren();
    document.body.classList.remove('overlay-open');
    setBackgroundInert(false);
    if (restore) modalFocus?.focus();
  }
  function trapTab(event, container) {
    const elements = all('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', container)
      .filter(element => element.getClientRects().length);
    if (!elements.length) return;
    const first = elements[0], last = elements.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') { closeVaytrixCheckout(); closeDrawer(); closeModal(); one('[data-product-lightbox][open]')?.close(); const menu = one('#mobile-nav'); if (menu && !menu.hidden) { menu.hidden = true; const toggle = one('.mobile-menu-toggle'); toggle?.setAttribute('aria-expanded', 'false'); toggle?.setAttribute('aria-label', 'Open menu'); toggle?.focus(); } }
    if (event.key === 'Tab') {
      const checkoutDialog = one('[data-vaytrix-checkout-modal][open]');
      if (checkoutDialog) { trapTab(event, checkoutDialog); return; }
      const nav = one('#mobile-nav');
      if (nav && !nav.hidden) { trapTab(event, one('#site-header')); return; }
      if (modal && !modal.hidden) trapTab(event, modal);
      else if (drawer?.classList.contains('open')) trapTab(event, drawer);
    }
  });

  async function api(path, options = {}) {
    const response = await fetch(route(path), {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, ...options
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw Error(result.description || result.message || 'Please try again.');
    }
    return response.json();
  }
  async function refreshCart() {
    const cart = await api('cart.js');
    all('[data-cart-count]').forEach(element => { element.textContent = cart.item_count; });
    const items = one('[data-cart-items]');
    if (items) items.innerHTML = cart.items.length ? cart.items.map((item, index) => {
      const image = item.featured_image?.url || item.image;
      const title = escapeHTML(item.product_title);
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<small>${escapeHTML(item.variant_title)}</small>` : '';
      const properties = Object.entries(item.properties || {}).filter(([key, value]) => !key.startsWith('_') && value).map(([key, value]) => `<small>${escapeHTML(key)}: ${escapeHTML(value)}</small>`).join('');
      return `<div class="cart-line"><a href="${escapeHTML(item.url)}">${image ? `<img src="${escapeHTML(image)}" width="100" height="120" alt="${title}" loading="lazy">` : title}</a><div><a href="${escapeHTML(item.url)}">${title}</a>${variant}${properties}<strong>${money(item.final_line_price)}</strong><label for="drawer-qty-${index}">Quantity</label><input id="drawer-qty-${index}" class="line-qty" type="number" min="0" inputmode="numeric" value="${item.quantity}" data-key="${escapeHTML(item.key)}"><button type="button" class="line-remove" data-key="${escapeHTML(item.key)}" aria-label="Remove ${title}">Remove</button></div></div>`;
    }).join('') : '<p>Your bag is empty.</p>';
    const subtotal = one('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = money(cart.total_price);
    const threshold = Number(document.body.dataset.shippingThreshold) * 100;
    if (threshold > 0) {
      all('[data-shipping-progress]').forEach(element => {
        element.textContent = cart.total_price >= threshold ? 'Shipping threshold reached' : `${money(threshold - cart.total_price)} until the shipping threshold`;
      });
      all('[data-shipping-bar]').forEach(element => { element.value = Math.min(100, cart.total_price / threshold * 100); });
    }
  }
  async function addItem(id, quantity = 1) {
    if (!id || Number(quantity) < 1) throw Error('Choose an available option and quantity.');
    await api('cart/add.js', { method: 'POST', body: JSON.stringify({ items: [{ id: Number(id), quantity: Number(quantity) }] }) });
    await refreshCart();
    openDrawer(false);
    announce('Added to bag');
  }
  async function changeItem(button, quantity) {
    button.disabled = true;
    try {
      await api('cart/change.js', { method: 'POST', body: JSON.stringify({ id: button.dataset.key, quantity }) });
      await refreshCart();
      if (quantity === 0) one('[data-close-drawer]')?.focus();
      else all('#cart-drawer .line-qty').find(input => input.dataset.key === button.dataset.key)?.focus();
      announce(quantity ? 'Bag updated' : 'Item removed');
    } catch (error) { announce(error.message); await refreshCart().catch(() => {}); }
    finally { button.disabled = false; }
  }

  function checkoutNodes(scope) {
    const dialog = one('[data-vaytrix-checkout-modal]') || one('[data-vaytrix-checkout-modal]', scope?.closest('body') || document);
    return {
      dialog,
      mount: dialog && one('[data-vaytrix-stripe-mount]', dialog),
      error: dialog && one('[data-vaytrix-checkout-error]', dialog),
      success: dialog && one('[data-vaytrix-checkout-success]', dialog),
      variant: dialog && one('[data-vaytrix-checkout-variant]', dialog),
      price: dialog && one('[data-vaytrix-checkout-price]', dialog),
      order: dialog && one('[data-vaytrix-order-number]', dialog),
      successOrder: dialog && one('[data-vaytrix-success-order]', dialog)
    };
  }

  function closeVaytrixCheckout() {
    checkoutToken++;
    setCheckoutBusy(false);
    const { dialog, mount, error, success } = checkoutNodes();
    if (!dialog) return;
    if (activeEmbeddedCheckout?.destroy) {
      try { activeEmbeddedCheckout.destroy(); } catch { /* session teardown is best-effort */ }
    }
    activeEmbeddedCheckout = null;
    if (mount) mount.replaceChildren();
    if (error) { error.hidden = true; error.textContent = ''; }
    if (success) success.hidden = true;
    if (dialog.open) dialog.close();
    document.body.classList.remove('vaytrix-checkout-open');
  }

  async function showCheckoutSuccess(sessionId, orderNumber) {
    const { dialog, mount, error, success, successOrder } = checkoutNodes();
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('vaytrix-checkout-open');
    if (mount) mount.hidden = true;
    if (error) error.hidden = true;
    if (success) success.hidden = false;
    if (successOrder) successOrder.textContent = orderNumber ? `Order ${orderNumber}` : (sessionId ? 'Payment confirmed' : 'Order confirmed');
  }

  async function openVaytrixCheckout(scope, selectedVariant) {
    if (checkoutBusy) return;
    const form = one('.product-form', scope);
    const variantId = one('[data-variant-input]', form)?.value;
    const { dialog, mount, error, success, variant, price, order } = checkoutNodes(scope);
    if (!dialog || !mount) return announce('Checkout is unavailable.');
    const publishableKey = dialog.dataset.stripePublishableKey?.trim();
    const apiBase = (dialog.dataset.apiBase || '').replace(/\/$/, '');
    if (!selectedVariant?.available || String(selectedVariant.id) !== variantId || !/^\d+$/.test(variantId || '')) return announce('Choose an available option first.');
    if (!publishableKey || !publishableKey.startsWith('pk_')) return announce('Stripe checkout is not configured yet.');
    if (!apiBase) return announce('Checkout backend is not configured yet.');
    const quantity = Number(one('[name="quantity"]', form)?.value || 1);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 10) return announce('Choose a quantity between 1 and 10.');
    const token = ++checkoutToken;
    setCheckoutBusy(true);

    one('.vaytrix-checkout-summary', dialog)?.classList.remove('is-cart');
    const title = one('[data-vaytrix-checkout-title]', dialog);
    if (title) title.textContent = one('h1', scope)?.textContent || 'Secure checkout';

    if (variant) variant.textContent = selectedVariant.title || selectedVariant.options?.join(' / ') || '';
    if (price) price.textContent = 'Confirming price…';
    if (order) order.textContent = '';
    if (error) { error.hidden = true; error.textContent = ''; }
    if (success) success.hidden = true;
    mount.hidden = false;
    mount.replaceChildren();
    if (activeEmbeddedCheckout?.destroy) {
      try { activeEmbeddedCheckout.destroy(); } catch { /* best effort */ }
      activeEmbeddedCheckout = null;
    }
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('vaytrix-checkout-open');

    try {
      const Stripe = await loadStripeJs();
      if (token !== checkoutToken) return;
      const stripe = Stripe(publishableKey);
      let secretPromise;
      const fetchClientSecret = () => secretPromise ||= (async () => {
        if (token !== checkoutToken) throw new Error('Checkout was closed.');
        const response = await fetch(`${apiBase}/api/checkout/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ variantId: `gid://shopify/ProductVariant/${variantId}`, quantity, country: dialog.dataset.marketCountry })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.clientSecret) throw new Error(payload.error || 'Checkout could not be started.');
        if (token !== checkoutToken) throw new Error('Checkout was closed.');
        if (variant && payload.variant) variant.textContent = payload.variant;
        if (price && Number.isFinite(payload.amount) && payload.currency) {
          price.textContent = checkoutMoney(payload.amount, payload.currency);
        }
        if (order && payload.orderNumber) order.textContent = `Order ${payload.orderNumber}`;
        dialog.dataset.sessionId = payload.sessionId || '';
        dialog.dataset.orderNumber = payload.orderNumber || '';
        return payload.clientSecret;
      })();
      const embedded = await stripe.initEmbeddedCheckout({ fetchClientSecret });
      if (token !== checkoutToken) { embedded.destroy?.(); return; }
      activeEmbeddedCheckout = embedded;
      embedded.mount(mount);
    } catch (checkoutError) {
      if (token !== checkoutToken) return;
      mount.hidden = true;
      if (error) { error.textContent = checkoutError.message || 'Checkout could not be loaded.'; error.hidden = false; }
      announce(checkoutError.message || 'Checkout could not be loaded.');
    } finally { if (token === checkoutToken) setCheckoutBusy(false); }
  }

  async function openCartCheckout() {
    if (checkoutBusy) return;
    const { dialog, mount, error, success, variant, price, order } = checkoutNodes();
    if (!dialog || !mount) return announce('Checkout is unavailable.');
    const publishableKey = dialog.dataset.stripePublishableKey?.trim();
    const apiBase = (dialog.dataset.apiBase || '').replace(/\/$/, '');
    if (!publishableKey?.startsWith('pk_') || !apiBase) return announce('Stripe checkout is not configured yet.');
    const token = ++checkoutToken;
    setCheckoutBusy(true);
    try {
      await cartUpdatePromise;
      const pageForm = one('.cart-page form[action]');
      if (pageForm) {
        const updates = all('input[name="updates[]"]', pageForm).map(input => Number(input.value));
        if (updates.some(quantity => !Number.isInteger(quantity) || quantity < 0)) throw Error('Enter valid bag quantities.');
        await api('cart/update.js', { method: 'POST', body: JSON.stringify({ updates, note: one('[name="note"]', pageForm)?.value || '' }) });
      }
      const cart = await api('cart.js');
      if (!cart.items?.length) throw Error('Your bag is empty.');
      if (cart.total_discount > 0 || cart.items.some(item => item.final_price < item.original_price)) {
        throw Error('Shopify discounts cannot be applied to this secure checkout. Remove the discount before continuing.');
      }
      const quantities = new Map();
      cart.items.forEach(item => {
        const id = String(item.id);
        if (!/^\d+$/.test(id) || !Number.isInteger(item.quantity) || item.quantity < 1) throw Error('Your bag contains an invalid item.');
        quantities.set(id, (quantities.get(id) || 0) + item.quantity);
      });
      const items = [...quantities].map(([id, quantity]) => {
        if (quantity > 10) throw Error('A bag item exceeds the limit of 10.');
        return { variantId: `gid://shopify/ProductVariant/${id}`, quantity };
      });
      if (items.length > 50) throw Error('Your bag has too many items.');
      if (token !== checkoutToken) return;
      closeDrawer();
      if (activeEmbeddedCheckout?.destroy) activeEmbeddedCheckout.destroy();
      activeEmbeddedCheckout = null;
      mount.hidden = false;
      mount.replaceChildren();
      if (error) { error.hidden = true; error.textContent = ''; }
      if (success) success.hidden = true;
      if (variant) variant.textContent = 'Preparing secure checkout…';
      if (price) price.textContent = '';
      if (order) order.textContent = '';
      const summary = one('.vaytrix-checkout-summary', dialog);
      summary?.classList.add('is-cart');
      const title = one('[data-vaytrix-checkout-title]', dialog);
      if (title) title.textContent = 'Your bag';
      if (!dialog.open) dialog.showModal();
      document.body.classList.add('vaytrix-checkout-open');
      const Stripe = await loadStripeJs();
      if (token !== checkoutToken) return;
      const stripe = Stripe(publishableKey);
      let secretPromise;
      const embedded = await stripe.initEmbeddedCheckout({
        fetchClientSecret: () => secretPromise ||= (async () => {
          if (token !== checkoutToken) throw Error('Checkout was closed.');
          const response = await fetch(`${apiBase}/api/checkout/cart-session`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items, country: dialog.dataset.marketCountry })
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok || !payload.clientSecret) throw Error(payload.error || 'Bag checkout could not be started.');
          if (token !== checkoutToken) throw Error('Checkout was closed.');
          if (variant) variant.textContent = (payload.items || []).map(item =>
            `${item.quantity} × ${item.product}${item.variant && item.variant !== 'Default Title' ? ` — ${item.variant}` : ''}`).join('\n');
          if (price && Number.isFinite(payload.amount) && payload.currency) {
            price.textContent = checkoutMoney(payload.amount, payload.currency);
          }
          if (order && payload.orderNumber) order.textContent = `Order ${payload.orderNumber}`;
          dialog.dataset.sessionId = payload.sessionId || '';
          dialog.dataset.orderNumber = payload.orderNumber || '';
          return payload.clientSecret;
        })()
      });
      if (token !== checkoutToken) { embedded.destroy?.(); return; }
      activeEmbeddedCheckout = embedded;
      embedded.mount(mount);
    } catch (checkoutError) {
      if (token === checkoutToken) {
        if (!dialog.open) { dialog.showModal(); document.body.classList.add('vaytrix-checkout-open'); }
        mount.hidden = true;
        if (error) { error.textContent = checkoutError.message || 'Bag checkout could not be loaded.'; error.hidden = false; }
        announce(checkoutError.message || 'Bag checkout could not be loaded.');
      }
    } finally {
      if (token === checkoutToken) setCheckoutBusy(false);
    }
  }

  async function restoreCheckoutReturn() {
    const params = new URLSearchParams(location.search);
    if (params.get('vaytrix_checkout_return') !== '1') return;
    const sessionId = params.get('session_id');
    const { dialog, error } = checkoutNodes();
    if (!dialog || !sessionId) return;
    const apiBase = (dialog.dataset.apiBase || '').replace(/\/$/, '');
    try {
      const response = await fetch(`${apiBase}/api/checkout/session/${encodeURIComponent(sessionId)}`);
      const payload = await response.json();
      if (response.ok && payload.paymentStatus === 'paid') await showCheckoutSuccess(sessionId, payload.orderNumber);
      else throw new Error('Payment is still being confirmed.');
    } catch (returnError) {
      if (!dialog.open) dialog.showModal();
      document.body.classList.add('vaytrix-checkout-open');
      if (error) { error.textContent = returnError.message || 'We could not confirm the payment yet.'; error.hidden = false; }
    } finally {
      params.delete('vaytrix_checkout_return');
      params.delete('session_id');
      const clean = `${location.pathname}${params.toString() ? `?${params}` : ''}${location.hash}`;
      history.replaceState(history.state, '', clean);
    }
  }

  function setupProduct(scope) {
    const form = one('.product-form', scope);
    if (!form || form.dataset.initialized) return;
    form.dataset.initialized = 'true';
    let data;
    try { data = JSON.parse(one('[data-product-json]', scope)?.textContent || '{}'); } catch { data = {}; }
    const variants = data.variants || [];
    const inventory = data.inventory || {};
    const resolveVariant = window.VaytrixCommerce.createVariantResolver(variants);
    let selectedVariant = null;
    function updateVariant() {
      const selected = all('.variant-options', scope).map(fieldset => {
        const value = one('input:checked', fieldset)?.value;
        const output = one('[data-selected-option]', fieldset);
        if (output) output.textContent = value || '';
        return value;
      });
      const variant = resolveVariant(selected);
      selectedVariant = variant || null;
      const input = one('[data-variant-input]', form);
      if (input) { input.disabled = !variant; input.value = variant?.id || ''; }
      const button = one('.add-to-cart', form);
      if (button) {
        button.disabled = !variant?.available;
        button.textContent = variant ? (variant.available ? `Add to bag — ${money(variant.price)}` : 'Sold out') : 'Unavailable combination';
      }
      const sticky = one('[data-mobile-add]');
      if (sticky && scope.closest('#quick-view') === null) { sticky.disabled = !variant?.available; sticky.textContent = variant?.available ? 'Add to bag' : 'Sold out'; }
      const buyNow = one('[data-vaytrix-buy-now]', form);
      if (buyNow) {
        buyNow.disabled = !variant?.available;
        buyNow.textContent = !variant ? 'Buy now — Select option' : variant.available ? `Buy now — ${money(variant.price)}` : 'Buy now — Sold out';
      }
      const mobileBuy = one('[data-vaytrix-mobile-buy]');
      if (mobileBuy && scope.closest('#quick-view') === null) {
        mobileBuy.disabled = !variant?.available;
        mobileBuy.textContent = !variant ? 'Buy now — Select option' : variant.available ? `Buy now — ${money(variant.price)}` : 'Buy now — Sold out';
      }
      const stock = one('[data-stock-status]', scope);
      const details = inventory[variant?.id];
      if (stock) stock.textContent = !variant ? 'Select a valid option' : !variant.available ? 'Sold out' : details?.managed && details.policy === 'deny' && details.quantity > 0 && details.quantity <= Number(scope.dataset.lowStock || 5)
        ? `Only ${details.quantity} left` : 'Available to order';
      const price = one('[data-product-price]', scope);
      if (price) price.textContent = variant ? money(variant.price) : 'Unavailable combination';
      const compare = one('[data-product-compare]', scope);
      if (compare) { compare.hidden = !variant || !(variant.compare_at_price > variant.price); compare.textContent = compare.hidden ? '' : money(variant.compare_at_price); }
      const activeMedia = details?.mediaId && one(`[data-media-id="${details.mediaId}"]`, scope);
      if (activeMedia && scope.closest('#quick-view') === null && form.dataset.lastVariant && form.dataset.lastVariant !== String(variant.id)) {
        activeMedia.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }
      if (variant && scope.closest('#quick-view') === null) {
        const address = new URL(location.href);
        address.searchParams.set('variant', variant.id);
        history.replaceState(history.state, '', address);
      }
      form.dataset.lastVariant = variant?.id || '';
      if (checkoutBusy) all('[data-vaytrix-buy-now],[data-vaytrix-mobile-buy]', scope).forEach(control => { control.dataset.checkoutDisabled = String(!variant?.available); control.disabled = true; });
    }
    scope.addEventListener('change', event => { if (event.target.matches('.variant-options input')) updateVariant(); });
    one('[data-vaytrix-buy-now]', form)?.addEventListener('click', () => openVaytrixCheckout(scope, selectedVariant));
    if (scope.closest('#quick-view') === null) one('[data-vaytrix-mobile-buy]')?.addEventListener('click', () => openVaytrixCheckout(scope, selectedVariant));
    form.addEventListener('submit', async event => {
      event.preventDefault();
      const button = one('.add-to-cart', form);
      if (!button || button.disabled) return;
      button.disabled = true;
      try { await addItem(one('[data-variant-input]', form)?.value, one('[name="quantity"]', form)?.value || 1); }
      catch (error) { announce(error.message); }
      finally { updateVariant(); }
    });
    updateVariant();
  }
  all('[data-product-section]').forEach(setupProduct);

  document.addEventListener('click', async event => {
    if (event.target.closest('[data-cart-checkout]')) { event.preventDefault(); openCartCheckout(); return; }
    if (event.target.closest('[data-vaytrix-checkout-close]')) { closeVaytrixCheckout(); return; }
    if (event.target.matches('[data-vaytrix-checkout-modal]')) { closeVaytrixCheckout(); return; }
    if (event.target.closest('[data-open-drawer]')) { openDrawer(); return; }
    if (event.target.closest('[data-close-drawer],#drawer-backdrop')) { closeDrawer(); return; }
    if (event.target.closest('.modal-close')) { closeModal(); return; }
    if (event.target.closest('[data-close-lightbox]')) { event.target.closest('dialog')?.close(); return; }
    const zoom = event.target.closest('[data-zoom]');
    if (zoom && typeof HTMLDialogElement !== 'undefined') {
      const dialog = one('[data-product-lightbox]');
      if (dialog && dialog.showModal) {
        event.preventDefault();
        const image = one('img', dialog);
        image.src = zoom.href; image.alt = one('img', zoom)?.alt || 'Product image';
        dialog.showModal();
        return;
      }
    }
    const addButton = event.target.closest('.card-add');
    if (addButton) {
      event.preventDefault();
      addButton.disabled = true;
      try { await addItem(addButton.dataset.variant); } catch (error) { announce(error.message); }
      finally { addButton.disabled = false; }
      return;
    }
    const remove = event.target.closest('#cart-drawer .line-remove');
    if (remove) { cartUpdatePromise = cartUpdatePromise.then(() => changeItem(remove, 0)); await cartUpdatePromise; return; }
    const quickView = event.target.closest('.quick-view');
    if (!quickView || !modal) return;
    modalFocus = quickView;
    quickView.disabled = true;
    try {
      const response = await fetch(quickView.dataset.url);
      if (!response.ok) throw Error('Quick view is unavailable. Open the product page to continue.');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = one('.product-layout', page);
      if (!source) throw Error('Quick view is unavailable. Open the product page to continue.');
      const content = source.cloneNode(true);
      // A product page can be open beneath this dialog; prefix every imported ID and reference.
      all('[id]', content).forEach(element => {
        const oldId = element.id;
        const newId = `quick-${oldId}`;
        all(`[for="${CSS.escape(oldId)}"],[aria-controls="${CSS.escape(oldId)}"]`, content).forEach(reference => {
          if (reference.getAttribute('for') === oldId) reference.setAttribute('for', newId);
          if (reference.getAttribute('aria-controls') === oldId) reference.setAttribute('aria-controls', newId);
        });
        element.id = newId;
      });
      all('a[href^="#"]', content).forEach(link => { link.setAttribute('href', `#quick-${link.getAttribute('href').slice(1)}`); });
      all('.shopify-payment-button', content).forEach(element => element.remove());
      modal.replaceChildren();
      const close = document.createElement('button');
      close.type = 'button'; close.className = 'modal-close'; close.setAttribute('aria-label', 'Close quick view'); close.textContent = '×';
      modal.append(close, content);
      modal.hidden = false;
      document.body.classList.add('overlay-open');
      setBackgroundInert(true);
      close.focus();
      setupProduct(one('[data-product-section]', content));
    } catch (error) { announce(error.message); }
    finally { quickView.disabled = false; }
  });
  document.addEventListener('change', event => {
    if (event.target.matches('#cart-drawer .line-qty')) {
      const quantity = Number(event.target.value);
      if (Number.isInteger(quantity) && quantity >= 0) cartUpdatePromise = cartUpdatePromise.then(() => changeItem(event.target, quantity));
      else announce('Enter a valid quantity.');
    }
  });
  modal?.addEventListener('click', event => { if (event.target === modal) closeModal(); });
  one('[data-scroll-to-form]')?.addEventListener('click', () => {
    const form = one('[data-product-section] .product-form');
    form?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    one('.variant-options input', form)?.focus();
  });

  document.addEventListener('click', event => {
    const menuButton = event.target.closest('.mobile-menu-toggle');
    if (menuButton) {
      const menu = one('#mobile-nav');
      menu.hidden = !menu.hidden;
      menuButton.setAttribute('aria-expanded', String(!menu.hidden));
      menuButton.setAttribute('aria-label', menu.hidden ? 'Open menu' : 'Close menu');
    }
    const openMobileMenu = one('#mobile-nav');
    if (!menuButton && openMobileMenu && !openMobileMenu.hidden && !event.target.closest('#site-header')) {
      openMobileMenu.hidden = true;
      const toggle = one('.mobile-menu-toggle');
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.setAttribute('aria-label', 'Open menu');
    }
    const filterButton = event.target.closest('.filter-toggle');
    if (filterButton) {
      const filters = one('#collection-filters');
      filters.classList.toggle('open');
      filterButton.setAttribute('aria-expanded', String(filters.classList.contains('open')));
    }
  });
  one('#load-more')?.addEventListener('click', async event => {
    event.preventDefault();
    const button = event.currentTarget;
    button.setAttribute('aria-busy', 'true'); button.textContent = 'Loading…';
    try {
      const response = await fetch(button.href);
      if (!response.ok) throw Error('Could not load more products.');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      one('#collection-products').append(...all('.product-card', page));
      syncWishlist();
      const next = one('#load-more', page);
      if (next) button.href = next.href; else button.remove();
      if (button.isConnected) button.textContent = 'Load more';
      announce('More products loaded');
    } catch (error) { button.textContent = 'Try again'; announce(error.message); }
    finally { button.removeAttribute('aria-busy'); }
  });

  const recommendations = one('[data-recommendations-url]');
  if (recommendations) fetch(recommendations.dataset.recommendationsUrl)
    .then(response => { if (!response.ok) throw Error(); return response.text(); })
    .then(html => { const section = one('.section', new DOMParser().parseFromString(html, 'text/html')); if (section) recommendations.replaceWith(section); })
    .catch(() => {});

  const wishlistKey = 'vaytrix-wishlist';
  let saved = storage.read(wishlistKey);
  const syncWishlist = () => all('.wishlist-toggle[data-handle]').forEach(button => {
    button.setAttribute('aria-pressed', String(saved.includes(button.dataset.handle)));
    button.setAttribute('aria-label', `${saved.includes(button.dataset.handle) ? 'Remove' : 'Save'} ${button.dataset.title || 'product'} ${saved.includes(button.dataset.handle) ? 'from' : 'to'} wishlist`);
  });
  syncWishlist();
  document.addEventListener('click', event => {
    const button = event.target.closest('.wishlist-toggle');
    if (!button) return;
    saved = saved.includes(button.dataset.handle) ? saved.filter(handle => handle !== button.dataset.handle) : [...saved, button.dataset.handle];
    storage.write(wishlistKey, saved); syncWishlist();
    announce(saved.includes(button.dataset.handle) ? 'Saved to wishlist' : 'Removed from wishlist');
  });
  async function renderProducts(handles, container) {
    if (!container) return 0;
    const products = await Promise.all(handles.slice(0, 8).map(async handle => {
      try { const response = await fetch(route(`products/${encodeURIComponent(handle)}.js`)); return response.ok ? response.json() : null; }
      catch { return null; }
    }));
    const valid = products.filter(Boolean);
    container.innerHTML = valid.map(product => {
      const href = route(`products/${encodeURIComponent(product.handle)}`);
      return `<article class="product-card"><a class="product-card__image" href="${href}"><img loading="lazy" width="360" height="450" src="${escapeHTML(product.featured_image || '')}" alt="${escapeHTML(product.title)}"></a><div class="product-card__body"><a class="product-card__title" href="${href}">${escapeHTML(product.title)}</a><span class="price">${money(product.price)}</span></div></article>`;
    }).join('');
    return valid.length;
  }
  const wishlistItems = one('[data-wishlist-items]');
  if (wishlistItems) renderProducts(saved, wishlistItems).then(count => {
    one('[data-wishlist-empty]')?.toggleAttribute('hidden', count > 0);
  });
  const currentProduct = one('[data-product-section]');
  const recentKey = 'vaytrix-recent';
  const handle = currentProduct && location.pathname.includes('/products/') ? location.pathname.split('/').filter(Boolean).at(-1) : null;
  const previous = storage.read(recentKey).filter(item => item !== handle);
  if (handle) storage.write(recentKey, [handle, ...previous].slice(0, 8));
  const recentContainer = one('[data-recent-items]');
  if (recentContainer && previous.length) renderProducts(previous, recentContainer).then(count => {
    if (count) recentContainer.closest('.recently-viewed').hidden = false;
  });
  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-discount-form]');
    if (!form) return;
    event.preventDefault();
    const code = form.elements.discount.value.trim();
    if (code) location.assign(`${route('discount/')}${encodeURIComponent(code)}?redirect=${encodeURIComponent(route('cart'))}`);
  });

  const search = one('[data-predictive-search]');
  if (search) {
    const input = one('input[name="q"]', search);
    const results = one('[data-predictive-results]', search);
    let controller, timer;
    input.addEventListener('input', () => {
      clearTimeout(timer); controller?.abort();
      const query = input.value.trim();
      if (query.length < 2) { results.hidden = true; results.replaceChildren(); return; }
      timer = setTimeout(async () => {
        controller = new AbortController();
        try {
          const params = new URLSearchParams({ q: query, 'resources[type]': 'product,collection,query', 'resources[limit]': '5', 'resources[options][unavailable_products]': 'hide' });
          const response = await fetch(`${route('search/suggest.json')}?${params}`, { signal: controller.signal });
          if (!response.ok) throw Error();
          const data = (await response.json()).resources.results;
          if (input.value.trim() !== query) return;
          const entries = [
            ...(data.queries || []).map(item => ({ label: item.text, href: `${route('search')}?q=${encodeURIComponent(item.text)}` })),
            ...(data.collections || []).map(item => ({ label: item.title, href: item.url })),
            ...(data.products || []).map(item => ({ label: item.title, href: item.url }))
          ];
          results.innerHTML = entries.map(item => `<a href="${escapeHTML(item.href)}">${escapeHTML(item.label)}</a>`).join('');
          results.hidden = !entries.length;
        } catch (error) { if (error.name !== 'AbortError') results.hidden = true; }
      }, 180);
    });
    input.addEventListener('keydown', event => { if (event.key === 'Escape') results.hidden = true; });
    search.addEventListener('submit', () => {
      const query = input.value.trim();
      if (query) storage.write('vaytrix-searches', [query, ...storage.read('vaytrix-searches').filter(item => item !== query)].slice(0, 5));
    });
    const recent = one('[data-recent-searches]');
    if (recent) recent.innerHTML = storage.read('vaytrix-searches').map(term => `<a href="${route('search')}?q=${encodeURIComponent(term)}">${escapeHTML(term)}</a>`).join('');
  }

  one('[data-vaytrix-checkout-modal]')?.addEventListener('cancel', event => { event.preventDefault(); closeVaytrixCheckout(); });
  all('select[data-default]').forEach(select => { select.value = select.dataset.default; });

  let navigationObserver;
  function observeHeader() {
    navigationObserver?.disconnect();
    if (!header || !('ResizeObserver' in window)) return;
    const inner = one('.header-inner', header), navigation = one('.desktop-nav', header);
    const fitNavigation = () => {
      if (!inner || !navigation) return;
      const available = inner.clientWidth - [...inner.children].filter(el => el !== navigation).reduce((total, el) => total + el.getBoundingClientRect().width, 0) - 60;
      const required = [...navigation.children].reduce((total, el) => total + el.scrollWidth, 0) + Math.max(0, navigation.children.length - 1) * 18;
      header.classList.toggle('nav-compact', window.innerWidth >= 1181 && required > available);
    };
    navigationObserver = new ResizeObserver(fitNavigation);
    if (inner) navigationObserver.observe(inner);
  }
  observeHeader();
  restoreCheckoutReturn().catch(() => {});
  document.addEventListener('shopify:section:load', event => {
    all('[data-product-section]', event.target).forEach(setupProduct);
    if (one('#site-header', event.target)) { header = one('#site-header'); updateHeader(); observeHeader(); }
  });
  if (one('.cart-page [data-shipping-progress]')) refreshCart().catch(() => {});
})();
